<script setup>
import { useAnalysisStore } from '@/store/modules/analysis';
import { addContentLogAPI } from '@/api/logServiceApi';
import IconAnalysNav210 from '@/components/svg/IconAnalysNav210.vue';
import { computed } from 'vue';
import { calculateDDay } from '@/utils/dateFormatter';

const analysisStore = useAnalysisStore();

const props = defineProps({
  guestUserId: {
    required: true,
  },
  personalityTicketData: {
    type: Object,
  },
  personalityLogData: {
    required: true,
  },
  hasPermission: {
    required: false,
  },
});

const emit = defineEmits(['goPopAnalysis']);

const personalityLogData = computed(() => {
  if (!props.personalityLogData?.length) return null;
  const personalityData = props.personalityLogData[0];
  return personalityData;
});

const dDay = computed(() => calculateDDay(personalityLogData.value?.expiryDate, false));

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

const formattedCreatedAt = computed(() => formatDate(personalityLogData.value?.createdAt));
const formattedExpiryDate = computed(() => formatDate(personalityLogData.value?.expiryDate));

const goProduct = () => {
  router.push('/purchase/tickets');
};

const goPopAnalysis = () => {
  // 상세 흐름의 '성향분석 시작하기' 발화 누락 보완.
  // 성향분석은 별도 _btn_start_ 타입이 없어 _btn_guide_ 타입 재사용(admin 조회수 집계 동일).
  // 개요 화면 '안내서 보기' 버튼과 화면 조건이 배타적이라 중복 없음.
  addContentLogAPI({
    contentType: props.hasPermission ? 'ap_personality_btn_guide_paid' : 'ap_personality_btn_guide_free',
  });
  emit('goPopAnalysis');
};

const clickGoPersonalityButton = () => {
  addContentLogAPI({
    contentType: props.hasPermission ? 'ap_personality_btn_guide_paid' : 'ap_personality_btn_guide_free',
  });
  analysisStore.navigateToContent('/personality', 0);
};
</script>

<template>
  <div class="overview-payment-info-wrap">
    <div class="overview-payment-inner">
      <div class="overview-none-wrap" v-if="!hasPermission">
        <div class="overview-none-title">
          <i class="ticket-icon__analysis"></i>
          <span class="tit">보유 성향분석권</span>
          <span class="cnt">{{ personalityTicketData?.totalCount || 0 }} 건</span>
        </div>
      </div>

      <!-- 분석권으로 이용 중일 때 -->
      <div class="overview-payment-info-content-box" v-else>
        <div class="overview-payment-info-content">
          <div class="overview-payment-info-title">낭만 성향분석을 이용하고 있어요!</div>
          <div class="overview-payment-info-date-box">
            <div class="overview-payment-info-date">
              <div class="overview-payment-info-date-title">시작일</div>
              <div class="overview-payment-info-date-value">{{ formattedCreatedAt || '-' }}</div>
            </div>
            <div class="overview-payment-info-date">
              <div class="overview-payment-info-date-title">만료일</div>
              <div class="overview-payment-info-date-value" v-if="personalityLogData?.expiryDate">
                {{ formattedExpiryDate }}
              </div>
              <div class="overview-payment-info-date-value" v-else>-</div>
            </div>
            <div class="overview-payment-info-date">
              <div class="overview-payment-info-date-title">남은 기간</div>
              <div
                class="overview-payment-info-date-value red"
                v-if="personalityLogData?.expiryDate"
              >
                {{ dDay }} 일 남음
              </div>
              <div class="overview-payment-info-date-value red" v-else>소장용</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 성향분석 소개 -->
      <div class="overview-pay-info-box">
        <div class="tit-box"><IconAnalysNav210 /> <span>낭만성향분석은?</span></div>
        <div class="txt-box">
          <div class="txt">
            <span class="txt-bold">사주명리학의 통찰, 인공지능의 분석력,</span> 그리고<br />
            <span class="txt-bold">심리학적 성향 해석 방식</span>을 결합하여 현실적인<br />
            성향 분석 도구로 개발되었습니다.
          </div>
          <div class="txt">
            사주원국의 오행구조와 격국, 용신 분석을 바탕으로<br />
            <span class="txt-bold">타고난 기질의 중심 성향(기본성향코드)</span>과
            <span class="txt-bold">삶의 균형을</span><br />
            <span class="txt-bold">돕는 보완 에너지(용신코드)</span>를 함께 제시함으로써 보다<br />
            입체적이고 실제적인 자기 이해를 돕고자 합니다.
          </div>
        </div>
      </div>
    </div>

    <div class="overview-btn-box">
      <button
        v-if="analysisStore.selectedMenu === '/overviewTendency'"
        class="overview-pay-info-btn"
        @click="clickGoPersonalityButton"
      >
        성향분석 안내서 보기
      </button>
      <div
        v-if="
          analysisStore.selectedMenu.includes('/personality') &&
          analysisStore.personalityIndex === 3 &&
          !props.hasPermission
        "
        class="overview-payment-info-btn"
        @click="goPopAnalysis"
      >
        성향분석 시작하기
      </div>
      <!-- <div class="overview-payment-info-btn-line" @click="goProduct">상품 소개 보러 가기</div> -->
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import 'src/assets/nangman_style/_analysis_main_color.scss';
.overview-none-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  .overview-none-title {
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 20px 28px;
    border-radius: 12px;
    border: 1px solid var(--Gray-300, #ebebeb);
    background: var(--gray-100-white, #fff);
    .tit {
      color: var(--Gray-800, #333);
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px; /* 160% */
    }
    .cnt {
      color: var(--orange-300-main, #eb4c10);
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px; /* 171.429% */
      margin-left: auto;
    }
  }
}
.overview-payment-info-wrap {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  height: 100%;
  box-sizing: border-box;
  width: 100%;
  padding: 28px;
  gap: 24px;
  overflow-y: scroll;
}

.overview-pay-info-box {
  display: flex;
  padding: 28px;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  border-radius: 12px;
  background: $analysis-personality-light;
  box-sizing: border-box;
  width: 100%;
  .tit-box {
    color: $analysis-personality;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px; /* 155.556% */
    display: flex;
    align-items: center;
    gap: 8px;

    span {
      display: inline-flex;
      align-items: center;
      line-height: inherit;
    }
  }
  .txt-box {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    color: var(--Gray-700, #575757);
    font-size: 15px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px; /* 160% */

    .txt-bold {
      font-weight: 700;
    }
  }
}

.overview-payment-info-content-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 20px;
}
.overview-payment-info {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 28px;
}
.overview-payment-info-content {
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--gray-100-white, #fff);
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 20px 28px;

  .overview-payment-info-title {
    width: 100%;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    color: var(--Gray-800, #333);
    font-size: 15px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px; /* 160% */
    margin-bottom: 8px;
  }

  .overview-payment-info-date-box {
    display: flex;
    width: 100%;
    justify-content: flex-start;
    align-items: flex-start;
    flex-direction: column;
  }
  .overview-payment-info-date {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: flex-start;
    .overview-payment-info-date-title {
      color: var(--Gray-800, #333);
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px; /* 171.429% */
      display: flex;
      align-items: center;
      gap: 4px;
    }
    .overview-payment-info-date-value {
      color: var(--Gray-800, #333);
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px; /* 171.429% */
      &.red {
        color: var(--orange-300-main, #eb4c10);
      }
    }
  }
}
.overview-btn-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 28px;
}
.overview-pay-info-btn {
  display: flex;
  padding: 14px 20px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  border-radius: 6px;
  border: 1px solid var(--orange-300-main, #eb4c10);
  background: var(--gray-100-white, #fff);
  color: var(--orange-300-main, #eb4c10);
  text-align: center;
  font-family: Pretendard;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
}
.overview-payment-info-btn {
  cursor: pointer;
  display: flex;
  width: 100%;
  padding: 14px 20px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  background: var(--orange-300-main, #eb4c10);
  color: var(--gray-100-white, #fff);
  text-align: center;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px; /* 133.333% */
}
.overview-payment-info-btn-line {
  cursor: pointer;
  color: var(--orange-300-main, #eb4c10);
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px; /* 128.571% */
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
}
.ticket-icon__analysis {
  background: url('@manseImages/ticket-icon.svg') center center no-repeat;
  background-size: contain;
  width: 20px;
  height: 20px;
  display: inline-block;
  vertical-align: middle;
  margin-right: 8px;
}

.overview-payment-inner {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
}
</style>
